import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Wrench, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getMaintenanceTypes, createMaintenanceType, updateMaintenanceType, deleteMaintenanceType } from '../../api';

const MaintenanceTypeManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', frequency: '', description: '' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: '', frequency: '', description: '' });

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getMaintenanceTypes();
      setTypes(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des types');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await createMaintenanceType({
        name: formData.name.trim(),
        frequency: formData.frequency ? parseInt(formData.frequency) : null,
        description: formData.description.trim() || null,
      });
      setFormData({ name: '', frequency: '', description: '' });
      setShowForm(false);
      fetchTypes();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Ce type existe déjà');
      } else {
        setError("Erreur lors de l'ajout");
        console.error(err);
      }
      setLoading(false);
    }
  };

  const startEdit = (type) => {
    setEditId(type.id);
    setEditData({
      name: type.name,
      frequency: type.frequency || '',
      description: type.description || '',
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData({ name: '', frequency: '', description: '' });
  };

  const saveEdit = async (id) => {
    if (!editData.name.trim()) { cancelEdit(); return; }
    try {
      setLoading(true);
      setError(null);
      await updateMaintenanceType(id, {
        name: editData.name.trim(),
        frequency: editData.frequency ? parseInt(editData.frequency) : null,
        description: editData.description.trim() || null,
      });
      cancelEdit();
      fetchTypes();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Ce nom existe déjà');
      } else {
        setError('Erreur lors de la modification');
        console.error(err);
      }
      setLoading(false);
    }
  };

  const handleDelete = async (type) => {
    const count = type._count?.interventions || 0;
    if (count > 0) {
      setError(`Impossible de supprimer "${type.name}" : ${count} intervention(s) liée(s).`);
      return;
    }
    if (!window.confirm(`Supprimer le type "${type.name}" ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteMaintenanceType(type.id);
      fetchTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  if (loading && types.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent>
            <p className="text-gray-500 py-8 text-center">Chargement...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench size={20} />
            Types de maintenance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {showForm && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="Nom du type *"
                  required
                />
                <input
                  type="number"
                  min="1"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.frequency}
                  onChange={(e) => setFormData(f => ({ ...f, frequency: e.target.value }))}
                  placeholder="Fréquence (mois)"
                />
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                  disabled={loading || !formData.name.trim()}
                >
                  <Check size={18} />
                  Créer
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setFormData({ name: '', frequency: '', description: '' }); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition"
                >
                  Annuler
                </button>
              </div>
            </form>
          )}

          <div>
            <h3 className="font-semibold text-gray-700 mb-3">
              Types ({types.length})
            </h3>
            {types.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucun type. Exécutez le seed ou ajoutez-en un ci-dessus.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Nom</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Fréquence</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700 hidden sm:table-cell">Description</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Interventions</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.map((type) => (
                      <tr key={type.id} className="border-t hover:bg-gray-50 transition">
                        <td className="px-3 py-2">
                          {editId === type.id ? (
                            <input
                              type="text"
                              className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                              value={editData.name}
                              onChange={(e) => setEditData(d => ({ ...d, name: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit(type.id);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              {type.color && (
                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: type.color }} />
                              )}
                              <span className="text-gray-800 font-medium">{type.name}</span>
                            </div>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          {editId === type.id ? (
                            <input
                              type="number"
                              min="1"
                              className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm text-center"
                              value={editData.frequency}
                              onChange={(e) => setEditData(d => ({ ...d, frequency: e.target.value }))}
                            />
                          ) : (
                            <span className="text-sm text-gray-600">
                              {type.frequency ? `${type.frequency} mois` : '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 hidden sm:table-cell">
                          {editId === type.id ? (
                            <input
                              type="text"
                              className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                              value={editData.description}
                              onChange={(e) => setEditData(d => ({ ...d, description: e.target.value }))}
                            />
                          ) : (
                            <span className="text-sm text-gray-500">{type.description || '-'}</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          <span className="text-sm text-gray-600">{type._count?.interventions || 0}</span>
                        </td>
                        <td className="text-center px-3 py-2">
                          <div className="flex justify-center gap-1">
                            {editId === type.id ? (
                              <>
                                <button onClick={() => saveEdit(type.id)} className="p-1 text-green-600 hover:bg-green-50 rounded-full transition" title="Enregistrer">
                                  <Check size={16} />
                                </button>
                                <button onClick={cancelEdit} className="p-1 text-gray-500 hover:bg-gray-200 rounded-full transition" title="Annuler">
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startEdit(type)} className="p-1 text-primary hover:bg-primary/5 rounded-full transition" title="Modifier" disabled={loading}>
                                  <Edit size={16} />
                                </button>
                                <button onClick={() => handleDelete(type)} className="p-1 text-red-600 hover:bg-red-50 rounded-full transition" title="Supprimer" disabled={loading}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter un type"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default MaintenanceTypeManagement;
